import React, {
    FormEvent,
    KeyboardEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useTranslation } from 'react-i18next';

import { useDnd } from '@shared/ui/DnD';
import { Icon } from '@shared/ui/Icon';
import { Lightbox } from '@shared/ui/Lightbox';

import { AssistantContactLinks } from './AssistantContactLinks';
import cls from './KnowledgeAssistant.module.css';
import { sendKnowledgeAssistantMessage } from '../api/sendKnowledgeAssistantMessage';
import {
    createLocalImageId,
    deleteExpiredLocalImages,
    getLocalImage,
    saveLocalImage,
} from '../lib/localImageStorage';
import { AssistantMessage, KnowledgeSource } from '../model/types';

import type { TFunction } from 'i18next';

const HISTORY_STORAGE_KEY = 'smetter.knowledge-assistant.history.v3';
const ASSISTANT_AVATAR_SRC = '/images/knowledge-assistant/alex-avatar.jpg';
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const ALLOWED_FILE_EXTENSIONS = [
    'pdf',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'csv',
    'txt',
    'png',
    'jpg',
    'jpeg',
];
const FILE_ICON_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'png', 'jpg'];
const IMAGE_FILE_EXTENSIONS = ['png', 'jpg', 'jpeg'];
const FILE_ACCEPT = ALLOWED_FILE_EXTENSIONS.map((extension) => `.${extension}`).join(',');

const normalizeLocale = (locale?: string) => (locale?.toLowerCase().startsWith('en') ? 'en' : 'ru');
const historyStorageKey = (locale: string) => `${HISTORY_STORAGE_KEY}.${locale}`;
const fileExtension = (fileName: string) => fileName.split('.').pop()?.toLowerCase() ?? '';
const isImageFileName = (fileName: string) =>
    IMAGE_FILE_EXTENSIONS.includes(fileExtension(fileName));
const imageMimeType = (fileName: string) =>
    fileExtension(fileName) === 'png' ? 'image/png' : 'image/jpeg';
const fileIconSrc = (fileName: string) => {
    const extension = fileExtension(fileName);
    const iconExtension = extension === 'jpeg' ? 'jpg' : extension;

    return `/images/icons/extensions/${
        FILE_ICON_EXTENSIONS.includes(iconExtension) ? iconExtension : 'undefined'
    }.svg`;
};

const welcomeMessage = (t: TFunction): AssistantMessage => ({
    id: 'welcome',
    role: 'assistant',
    localOnly: true,
    createdAt: Date.now(),
    content: t('KnowledgeAssistant.welcome'),
});

const createMessage = (
    role: AssistantMessage['role'],
    content: string,
    options: Partial<AssistantMessage> = {}
): AssistantMessage => ({
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    role,
    content,
    createdAt: Date.now(),
    ...options,
});

const relativeTime = (createdAt: number, now: number, t: TFunction) => {
    const elapsedMinutes = Math.max(0, Math.floor((now - createdAt) / 60000));
    if (elapsedMinutes < 1) return t('KnowledgeAssistant.time.justNow');
    if (elapsedMinutes < 60) {
        return t('KnowledgeAssistant.time.minutesAgo', { count: elapsedMinutes });
    }

    const elapsedHours = Math.floor(elapsedMinutes / 60);
    if (elapsedHours < 24) {
        return t('KnowledgeAssistant.time.hoursAgo', { count: elapsedHours });
    }

    return t('KnowledgeAssistant.time.daysAgo', { count: Math.floor(elapsedHours / 24) });
};

const renderInlineMarkdown = (content: string) =>
    content.split(/(\*\*[^*\n]+\*\*)/g).map((part, index) =>
        part.startsWith('**') && part.endsWith('**') ? (
            <strong key={`${part}-${index}`}>{part.slice(2, -2)}</strong>
        ) : (
            <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
        )
    );

const AssistantAnswer = ({ content }: { content: string }) => (
    <>
        {content.split('\n').map((line, index, lines) => (
            <React.Fragment key={`${line}-${index}`}>
                {renderInlineMarkdown(line)}
                {index < lines.length - 1 && <br />}
            </React.Fragment>
        ))}
    </>
);

const AttachmentIcon = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M10 17.5C7.5875 17.5 5.625 15.5375 5.625 13.125V5.625C5.625 3.9 7.025 2.5 8.75 2.5C10.475 2.5 11.875 3.9 11.875 5.625V13.125C11.875 14.1562 11.0313 15 10 15C8.96875 15 8.125 14.1562 8.125 13.125V6.25H9.375V13.125C9.375 13.4688 9.65625 13.75 10 13.75C10.3438 13.75 10.625 13.4688 10.625 13.125V5.625C10.625 4.59375 9.78125 3.75 8.75 3.75C7.71875 3.75 6.875 4.59375 6.875 5.625V13.125C6.875 14.85 8.275 16.25 10 16.25C11.725 16.25 13.125 14.85 13.125 13.125V4.375H14.375V13.125C14.375 15.5375 12.4125 17.5 10 17.5Z"
            fill="#1094F7"
        />
    </svg>
);

interface ImagePreviewFile {
    id: number;
    fileSrc: string;
    fileName: string;
    mimeType: string;
}

interface MessageAttachmentPreviewProps {
    attachment: NonNullable<AssistantMessage['attachment']>;
    onOpen: (fileSrc: string, fileName: string, mimeType: string) => void;
}

const MessageAttachmentPreview = ({ attachment, onOpen }: MessageAttachmentPreviewProps) => {
    const [storedPreview, setStoredPreview] = useState<{
        imageId: string;
        imageUrl: string;
    } | null>(null);

    useEffect(() => {
        const imageId = attachment.localImageId;
        if (!imageId) {
            setStoredPreview(null);
            return () => undefined;
        }

        let isActive = true;
        let objectUrl: string | null = null;

        void getLocalImage(imageId)
            .then((blob) => {
                if (!blob || !isActive) return;
                objectUrl = URL.createObjectURL(blob);
                setStoredPreview({
                    imageId,
                    imageUrl: objectUrl,
                });
            })
            .catch(() => {
                if (isActive) setStoredPreview(null);
            });

        return () => {
            isActive = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [attachment.localImageId]);

    const imageUrl =
        storedPreview && storedPreview.imageId === attachment.localImageId
            ? storedPreview.imageUrl
            : null;

    if (imageUrl) {
        return (
            <button
                type="button"
                className={`${cls.sentAttachmentPreview} ${cls.imagePreviewButton}`}
                title={attachment.name}
                onClick={() =>
                    onOpen(
                        imageUrl,
                        attachment.name,
                        attachment.mimeType ?? imageMimeType(attachment.name)
                    )
                }
            >
                <img className={cls.imageThumbnail} src={imageUrl} alt={attachment.name} />
            </button>
        );
    }

    return (
        <div className={cls.sentAttachmentPreview} title={attachment.name}>
            <img src={fileIconSrc(attachment.name)} alt={attachment.name} />
        </div>
    );
};

const useImageObjectUrl = (file: File | null) => {
    const [preview, setPreview] = useState<{ file: File; imageUrl: string } | null>(null);

    useEffect(() => {
        if (!file || !isImageFileName(file.name)) {
            setPreview(null);
            return () => undefined;
        }

        const objectUrl = URL.createObjectURL(file);
        setPreview({ file, imageUrl: objectUrl });

        return () => URL.revokeObjectURL(objectUrl);
    }, [file]);

    return preview?.file === file ? preview.imageUrl : null;
};

const isSafeSource = (source: unknown): source is KnowledgeSource => {
    if (
        !source ||
        typeof source !== 'object' ||
        !('id' in source) ||
        !('title' in source) ||
        !('url' in source) ||
        typeof source.id !== 'string' ||
        typeof source.title !== 'string' ||
        typeof source.url !== 'string'
    ) {
        return false;
    }

    try {
        const url = new URL(source.url);
        return (
            url.protocol === 'https:' &&
            ['help.smetter.ru', 'help.smetter.com'].includes(url.hostname)
        );
    } catch {
        return false;
    }
};

const loadHistory = (t: TFunction, locale: string): AssistantMessage[] => {
    try {
        const stored = window.sessionStorage.getItem(historyStorageKey(locale));
        if (!stored) return [welcomeMessage(t)];
        const parsed = JSON.parse(stored);
        if (!Array.isArray(parsed)) return [welcomeMessage(t)];

        const messages = parsed.filter(
            (message): message is AssistantMessage =>
                message &&
                typeof message.id === 'string' &&
                ['user', 'assistant'].includes(message.role) &&
                typeof message.content === 'string'
        );

        return [
            welcomeMessage(t),
            ...messages.slice(-12).map((message) => ({
                ...message,
                createdAt:
                    typeof message.createdAt === 'number' ? message.createdAt : Date.now(),
                attachment:
                    message.attachment && typeof message.attachment.name === 'string'
                        ? {
                              name: message.attachment.name.slice(0, 255),
                              localImageId:
                                  typeof message.attachment.localImageId === 'string'
                                      ? message.attachment.localImageId.slice(0, 255)
                                      : undefined,
                              mimeType:
                                  typeof message.attachment.mimeType === 'string'
                                      ? message.attachment.mimeType.slice(0, 100)
                                      : undefined,
                          }
                        : undefined,
                sources: Array.isArray(message.sources)
                    ? message.sources.filter(isSafeSource)
                    : undefined,
            })),
        ];
    } catch {
        return [welcomeMessage(t)];
    }
};

export const KnowledgeAssistant = () => {
    const { t, i18n } = useTranslation();
    const locale = normalizeLocale(i18n.resolvedLanguage ?? i18n.language);
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<AssistantMessage[]>(() => loadHistory(t, locale));
    const [question, setQuestion] = useState('');
    const [attachedFile, setAttachedFile] = useState<File | null>(null);
    const [attachmentError, setAttachmentError] = useState<string | null>(null);
    const [isSending, setIsSending] = useState(false);
    const [now, setNow] = useState(Date.now());
    const [imagePreviewFile, setImagePreviewFile] = useState<ImagePreviewFile | null>(null);
    const assistantRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const ignoreOutsideClickRef = useRef(false);

    const attachedImageUrl = useImageObjectUrl(attachedFile);

    useEffect(() => {
        void deleteExpiredLocalImages().catch(() => undefined);
    }, []);

    useEffect(() => {
        if (!isOpen) setImagePreviewFile(null);
    }, [isOpen]);

    const openImagePreview = useCallback(
        (fileSrc: string, fileName: string, mimeType: string) => {
            setImagePreviewFile({
                id: Date.now(),
                fileSrc,
                fileName,
                mimeType,
            });
        },
        []
    );

    const closeImagePreview = useCallback(() => {
        ignoreOutsideClickRef.current = true;
        setImagePreviewFile(null);
        window.setTimeout(() => {
            ignoreOutsideClickRef.current = false;
        }, 0);
    }, []);

    const selectFile = useCallback(
        (files: File[]) => {
            if (files.length === 0) return;
            if (files.length !== 1) {
                setAttachmentError(t('KnowledgeAssistant.files.onlyOne'));
                return;
            }

            const [file] = files;
            if (!ALLOWED_FILE_EXTENSIONS.includes(fileExtension(file.name))) {
                setAttachmentError(t('KnowledgeAssistant.files.invalidType'));
                return;
            }
            if (file.size > MAX_FILE_SIZE_BYTES) {
                setAttachmentError(t('KnowledgeAssistant.files.tooLarge'));
                return;
            }

            setAttachedFile(file);
            setAttachmentError(null);
        },
        [t]
    );
    const {
        isFileOver,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleInputChange,
        clickFileInput,
    } = useDnd({
        attachmentsData: attachedFile ? [attachedFile] : [],
        isDisabled: isSending || attachedFile !== null,
        onUploadHandler: selectFile,
        fileInputRef,
    });

    const history = useMemo(
        () => messages.filter(({ localOnly, isError }) => !localOnly && !isError).slice(-6),
        [messages]
    );
    const starterQuestions = [
        t('KnowledgeAssistant.starters.creatingEstimates'),
        t('KnowledgeAssistant.starters.collaboratingOnEstimates'),
        t('KnowledgeAssistant.starters.purchasingMaterial'),
    ];
    const showStarterQuestions = messages.length === 1 && messages[0].localOnly === true;

    useEffect(() => {
        const storedMessages = messages.filter(({ localOnly }) => !localOnly).slice(-12);
        try {
            window.sessionStorage.setItem(
                historyStorageKey(locale),
                JSON.stringify(storedMessages)
            );
        } catch {
            // Блокировка sessionStorage не должна отключать сам ассистент.
        }
    }, [locale, messages]);

    useEffect(() => {
        if (!isOpen) return () => undefined;
        setNow(Date.now());
        const interval = window.setInterval(() => setNow(Date.now()), 30000);

        return () => window.clearInterval(interval);
    }, [isOpen]);

    useEffect(() => {
        const trigger = document.querySelector<HTMLElement>('[data-knowledge-assistant-trigger]');
        if (!trigger) return () => undefined;

        triggerRef.current = trigger;
        const toggleAssistant = () => setIsOpen((open) => !open);
        const toggleAssistantOnKeyboard = (event: globalThis.KeyboardEvent) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            toggleAssistant();
        };

        trigger.addEventListener('click', toggleAssistant);
        trigger.addEventListener('keydown', toggleAssistantOnKeyboard);

        return () => {
            trigger.setAttribute('aria-expanded', 'false');
            trigger.removeEventListener('click', toggleAssistant);
            trigger.removeEventListener('keydown', toggleAssistantOnKeyboard);
            triggerRef.current = null;
        };
    }, []);

    useEffect(() => {
        const trigger = triggerRef.current;
        if (!trigger) return () => undefined;

        trigger.setAttribute('aria-expanded', String(isOpen));
        if (!isOpen) return () => undefined;

        const closeOnOutsideClick = (event: globalThis.MouseEvent) => {
            if (!(event.target instanceof Node)) return;
            if (
                ignoreOutsideClickRef.current ||
                imagePreviewFile ||
                assistantRef.current?.contains(event.target) ||
                trigger.contains(event.target)
            )
                return;

            setIsOpen(false);
        };

        document.addEventListener('click', closeOnOutsideClick);

        return () => document.removeEventListener('click', closeOnOutsideClick);
    }, [imagePreviewFile, isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        textareaRef.current?.focus();
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [isOpen, messages, isSending]);

    useEffect(() => {
        const closeOnEscape = (event: globalThis.KeyboardEvent) => {
            if (event.key === 'Escape') setIsOpen(false);
        };
        window.addEventListener('keydown', closeOnEscape);

        return () => {
            window.removeEventListener('keydown', closeOnEscape);
            abortControllerRef.current?.abort();
        };
    }, []);

    const submit = async (event?: FormEvent, questionOverride?: string) => {
        event?.preventDefault();
        const normalizedQuestion = (questionOverride ?? question).trim();
        if (!normalizedQuestion || isSending) return;

        const file = attachedFile;
        const localImageId = file && isImageFileName(file.name) ? createLocalImageId() : undefined;
        const message = createMessage('user', normalizedQuestion, {
            attachment: file
                ? {
                      name: file.name,
                      localImageId,
                      mimeType: localImageId ? file.type || imageMimeType(file.name) : undefined,
                  }
                : undefined,
        });
        const requestHistory = history;
        setMessages((current) => [...current, message]);
        setQuestion('');
        setAttachedFile(null);
        setAttachmentError(null);
        setIsSending(true);
        if (file && localImageId) {
            void saveLocalImage(localImageId, file).catch(() => {
                setMessages((current) =>
                    current.map((currentMessage) =>
                        currentMessage.id === message.id && currentMessage.attachment
                            ? {
                                  ...currentMessage,
                                  attachment: { name: currentMessage.attachment.name },
                              }
                            : currentMessage
                    )
                );
            });
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const result = await sendKnowledgeAssistantMessage({
                question: normalizedQuestion,
                history: requestHistory,
                file,
                signal: controller.signal,
            });
            setMessages((current) => [
                ...current,
                createMessage('assistant', result.answer, {
                    sources: result.sources.filter(isSafeSource),
                }),
            ]);
        } catch {
            if (controller.signal.aborted) return;
            setMessages((current) => [
                ...current,
                createMessage(
                    'assistant',
                    t('KnowledgeAssistant.requestError'),
                    { localOnly: true, isError: true }
                ),
            ]);
        } finally {
            if (!controller.signal.aborted) setIsSending(false);
            if (abortControllerRef.current === controller) abortControllerRef.current = null;
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();
            void submit();
        }
    };

    return (
        <div ref={assistantRef} className={cls.root}>
            {isOpen && (
                <>
                    <section
                        id="knowledge-assistant-dialog"
                        className={cls.panel}
                        role="dialog"
                        aria-modal="false"
                        aria-label={t('KnowledgeAssistant.dialogLabel')}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <header className={cls.header}>
                            <strong>{t('KnowledgeAssistant.title')}</strong>
                            <button
                                type="button"
                                className={cls.closeButton}
                                onClick={() => setIsOpen(false)}
                                aria-label={t('KnowledgeAssistant.close')}
                            >
                                <Icon iconName="close" fill="currentColor" />
                            </button>
                        </header>

                        <div className={cls.messages} aria-live="polite">
                            {messages.map((message) => {
                                const hasSources = !!message.sources?.length;
                                const isWelcome = message.id === 'welcome';

                                if (message.role === 'user') {
                                    return (
                                        <div
                                            key={message.id}
                                            className={`${cls.messageRow} ${cls.userRow}`}
                                        >
                                            <div className={cls.userMessageContent}>
                                                {message.attachment && (
                                                    <MessageAttachmentPreview
                                                        attachment={message.attachment}
                                                        onOpen={openImagePreview}
                                                    />
                                                )}
                                                <div
                                                    className={`${cls.message} ${cls.userMessage}`}
                                                >
                                                    {message.content}
                                                </div>
                                            </div>
                                            <span className={cls.messageTime}>
                                                {relativeTime(message.createdAt, now, t)}
                                            </span>
                                        </div>
                                    );
                                }

                                return (
                                    <div
                                        key={message.id}
                                        className={`${cls.messageRow} ${cls.assistantRow}`}
                                    >
                                        <img
                                            className={cls.assistantAvatar}
                                            src={ASSISTANT_AVATAR_SRC}
                                            alt=""
                                        />
                                        <div className={cls.messageBody}>
                                            <span className={cls.messageAuthor}>
                                                {t('KnowledgeAssistant.assistantName')}
                                            </span>
                                            <div
                                                className={`${cls.message} ${
                                                    hasSources || isWelcome
                                                        ? cls.messageSegmentTop
                                                        : ''
                                                } ${message.isError ? cls.errorMessage : ''}`}
                                            >
                                                <AssistantAnswer content={message.content} />
                                            </div>
                                            {isWelcome && (
                                                <div
                                                    className={`${cls.message} ${cls.messageSegmentBottom}`}
                                                >
                                                    {t('KnowledgeAssistant.disclaimer')}
                                                </div>
                                            )}
                                            {hasSources && (
                                                <div className={cls.sources}>
                                                    <span>
                                                        {t('KnowledgeAssistant.sourcesLabel')}
                                                    </span>
                                                    {message.sources?.map((source) => (
                                                        <a
                                                            key={source.id}
                                                            href={source.url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            {source.title}
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                            <span className={cls.messageTime}>
                                                {relativeTime(message.createdAt, now, t)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                            {isSending && (
                                <div className={`${cls.messageRow} ${cls.assistantRow}`}>
                                    <img
                                        className={cls.assistantAvatar}
                                        src={ASSISTANT_AVATAR_SRC}
                                        alt=""
                                    />
                                    <div className={cls.messageBody}>
                                        <span className={cls.messageAuthor}>
                                            {t('KnowledgeAssistant.assistantName')}
                                        </span>
                                        <div className={`${cls.message} ${cls.typing}`}>
                                            <span />
                                            <span />
                                            <span />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!isSending && showStarterQuestions && (
                                <div
                                    className={cls.suggestions}
                                    aria-label={t('KnowledgeAssistant.suggestions')}
                                >
                                    {starterQuestions.map((suggestion) => (
                                        <button
                                            type="button"
                                            className={cls.suggestion}
                                            key={suggestion}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                void submit(undefined, suggestion);
                                            }}
                                        >
                                            {suggestion}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        <form className={cls.composer} onSubmit={submit}>
                            <button
                                type="button"
                                className={cls.attachmentButton}
                                onClick={clickFileInput}
                                disabled={isSending || attachedFile !== null}
                                aria-label={t('KnowledgeAssistant.files.attach')}
                            >
                                <AttachmentIcon />
                            </button>
                            <div className={cls.composerInput}>
                                {attachedFile && (
                                    <div className={cls.attachmentPreview} title={attachedFile.name}>
                                        {attachedImageUrl ? (
                                            <button
                                                type="button"
                                                className={cls.imagePreviewButton}
                                                onClick={() =>
                                                    openImagePreview(
                                                        attachedImageUrl,
                                                        attachedFile.name,
                                                        attachedFile.type ||
                                                            imageMimeType(attachedFile.name)
                                                    )
                                                }
                                            >
                                                <img
                                                    className={cls.imageThumbnail}
                                                    src={attachedImageUrl}
                                                    alt={attachedFile.name}
                                                />
                                            </button>
                                        ) : (
                                            <img
                                                src={fileIconSrc(attachedFile.name)}
                                                alt={attachedFile.name}
                                            />
                                        )}
                                        <button
                                            type="button"
                                            className={cls.removeAttachmentButton}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setAttachedFile(null);
                                                setAttachmentError(null);
                                            }}
                                            disabled={isSending}
                                            aria-label={t('KnowledgeAssistant.files.remove', {
                                                name: attachedFile.name,
                                            })}
                                        >
                                            <Icon
                                                iconName="close"
                                                fill="currentColor"
                                                width={10}
                                                height={10}
                                            />
                                        </button>
                                    </div>
                                )}
                                {attachmentError && (
                                    <span className={cls.attachmentError} role="alert">
                                        {attachmentError}
                                    </span>
                                )}
                                <textarea
                                    ref={textareaRef}
                                    value={question}
                                    onChange={(event) =>
                                        setQuestion(event.target.value.slice(0, 2000))
                                    }
                                    onKeyDown={handleKeyDown}
                                    rows={1}
                                    placeholder={t('KnowledgeAssistant.placeholder')}
                                    aria-label={t('KnowledgeAssistant.questionLabel')}
                                    disabled={isSending}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={!question.trim() || isSending}
                                aria-label={t('KnowledgeAssistant.send')}
                            >
                                <Icon iconName="send" fill="currentColor" width={18} height={18} />
                            </button>
                            <input
                                ref={fileInputRef}
                                className={cls.fileInput}
                                type="file"
                                accept={FILE_ACCEPT}
                                onChange={handleInputChange}
                                disabled={isSending || attachedFile !== null}
                            />
                        </form>
                        {isFileOver && (
                            <div className={cls.dropOverlay} aria-hidden="true">
                                <span className={cls.dropHint}>
                                    {t('KnowledgeAssistant.files.dropHint')}
                                </span>
                            </div>
                        )}
                    </section>
                    <AssistantContactLinks />
                    {imagePreviewFile && (
                        <Lightbox
                            files={[imagePreviewFile]}
                            currentFileId={imagePreviewFile.id}
                            onClose={closeImagePreview}
                        />
                    )}
                </>
            )}
        </div>
    );
};
